import { AnalysisErrorAlert as ErrorAlert } from './AnalysisErrorAlert.tsx';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, SelectInput, TextArea, TextInput } from '@lumlab/platform-ui';
import { arbitrationCommands, questionCategory } from '../contract/analysis-arbitration.ts';
import { analysisCommand } from './analysis-api.ts';

export function QuestionForm({ runId, onDone }: { runId: string; onDone: () => void }) {
    const { t } = useTranslation();
    const client = useQueryClient();
    const [category, setCategory] = useState('fact');
    const [fields, setFields] = useState('');
    const [prompt, setPrompt] = useState('');
    const [alternatives, setAlternatives] = useState('');
    const [nature, setNature] = useState('uncertain');
    const [importance, setImportance] = useState('normal');
    const [blocking, setBlocking] = useState(true);
    const [document, setDocument] = useState('');
    const [page, setPage] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [pending, setPending] = useState<{ key: string; command: unknown } | null>(null);
    const save = useMutation({
        mutationFn: analysisCommand,
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ['analysis'] });
            onDone();
        },
    });
    const schema = arbitrationCommands.options[0];
    return (
        <Card>
            <form
                className="space-y-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    const content = {
                        operation: 'open-question',
                        runId,
                        dataScope: 'public',
                        category,
                        fields: fields
                            .split(',')
                            .map((field) => field.trim())
                            .filter(Boolean),
                        prompt,
                        nature,
                        importance,
                        blocking,
                        alternatives: alternatives
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        evidence: document && excerpt ? [{ document, page: page ? Number(page) : null, excerpt }] : [],
                    };
                    const key = JSON.stringify(content);
                    const command = pending?.key === key ? pending.command : { ...content, id: crypto.randomUUID() };
                    setPending({ key, command });
                    save.mutate(command);
                }}
            >
                <SelectInput
                    label={t('tenders.arbitration.categoryLabel')}
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                >
                    {questionCategory.options.map((value) => (
                        <option key={value} value={value}>
                            {t(`tenders.arbitration.category.${value}`)}
                        </option>
                    ))}
                </SelectInput>
                <SelectInput
                    label={t('tenders.arbitration.natureLabel')}
                    value={nature}
                    onChange={(event) => setNature(event.target.value)}
                >
                    {schema.shape.nature.options.map((value) => (
                        <option key={value} value={value}>
                            {t(`tenders.arbitration.nature.${value}`)}
                        </option>
                    ))}
                </SelectInput>
                <SelectInput
                    label={t('tenders.arbitration.importanceLabel')}
                    value={importance}
                    onChange={(event) => setImportance(event.target.value)}
                >
                    {schema.shape.importance.options.map((value) => (
                        <option key={value} value={value}>
                            {t(`tenders.arbitration.importance.${value}`)}
                        </option>
                    ))}
                </SelectInput>
                <TextInput
                    label={t('tenders.arbitration.fields')}
                    value={fields}
                    onChange={(event) => setFields(event.target.value)}
                    required
                    maxLength={1000}
                />
                <TextArea
                    label={t('tenders.arbitration.question')}
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    required
                    maxLength={20000}
                />
                <TextArea
                    label={t('tenders.arbitration.alternatives')}
                    value={alternatives}
                    onChange={(event) => setAlternatives(event.target.value)}
                    maxLength={20000}
                />
                <Checkbox
                    label={t('tenders.arbitration.blocking')}
                    checked={blocking}
                    onChange={(event) => setBlocking(event.target.checked)}
                />
                <TextInput
                    label={t('tenders.arbitration.document')}
                    value={document}
                    onChange={(event) => setDocument(event.target.value)}
                    maxLength={1000}
                />
                <TextInput
                    label={t('tenders.arbitration.pageLabel')}
                    type="number"
                    min={1}
                    value={page}
                    onChange={(event) => setPage(event.target.value)}
                />
                <TextArea
                    label={t('tenders.arbitration.excerpt')}
                    value={excerpt}
                    onChange={(event) => setExcerpt(event.target.value)}
                    required={Boolean(document)}
                    maxLength={20000}
                />
                {save.error && <ErrorAlert error={save.error} />}
                <Button type="submit" variant="default" loading={save.isPending}>
                    {t('tenders.arbitration.addQuestion')}
                </Button>
            </form>
        </Card>
    );
}
